// Onboarding piece of the TaxiCaller integration: this tenant's webhook
// URL (hand it to a new tenant's own TaxiCaller account, or configure it
// yourself against a shared one) plus the company-name -> tenant mapping
// table that lets ONE TaxiCaller account serve several company identities
// (today: Taxis Gold & Black, Taxis Gold & Black Feilding, Specialty
// Transport Services all report through the one TGB account, split by
// this field -- see tenant_taxicaller_companies / taxicaller_full_webhook's
// resolveTenantId). Same load-all/inline-edit/add-form shape as
// VehicleCheckItemsSettings.tsx and DriverNoticeBoardSettings.tsx.
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";
import { supabase } from "@/lib/supabase";
import { Plus, Save, X, Edit, Trash2, Webhook, Copy, Check } from "lucide-react";

// Every settings panel in this app scopes to this one tenant today -- see
// the same constant in TeamAccessSettings.tsx, DriverNoticeBoardSettings.tsx
// etc. Becomes "whichever tenant is logged in" once AppContext exposes a
// real tenantId (SAAS_IVR_ROADMAP.md Fix Now #4), not before -- this
// screen doesn't need to solve that on its own.
const DEFAULT_TENANT_ID = "1225f4cb-43ae-463d-a6b0-53f721362dbf";

const FUNCTIONS_BASE = `${(import.meta.env.VITE_SUPABASE_URL as string).replace(/\/$/, "")}/functions/v1`;
const WEBHOOK_URL = `${FUNCTIONS_BASE}/taxicaller_full_webhook?tenant_id=${DEFAULT_TENANT_ID}`;

interface CompanyRow {
  id: string;
  company_name: string;
  notes: string | null;
  is_active: boolean;
  created_at: string;
}

const emptyNewForm = { company_name: "", notes: "" };

const TaxiCallerCompaniesSettings: React.FC = () => {
  const [companies, setCompanies] = useState<CompanyRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<CompanyRow>>({});
  const [showAddForm, setShowAddForm] = useState(false);
  const [newForm, setNewForm] = useState(emptyNewForm);
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("tenant_taxicaller_companies")
      .select("*")
      .order("company_name", { ascending: true });

    if (error) {
      toast({ title: "Error", description: "Failed to load companies: " + error.message, variant: "destructive" });
    } else {
      setCompanies(data || []);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const copyWebhookUrl = async () => {
    try {
      await navigator.clipboard.writeText(WEBHOOK_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast({ title: "Couldn't copy", description: "Select and copy the URL manually.", variant: "destructive" });
    }
  };

  const startEdit = (c: CompanyRow) => {
    setEditingId(c.id);
    setEditForm({ ...c });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEdit = async () => {
    if (!editingId || !editForm.company_name?.trim()) {
      toast({ title: "Error", description: "Company name is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("tenant_taxicaller_companies")
      .update({
        company_name: editForm.company_name.trim(),
        notes: editForm.notes?.trim() || null,
        is_active: editForm.is_active ?? true,
        updated_at: new Date().toISOString(),
      })
      .eq("id", editingId);

    if (error) {
      toast({ title: "Error", description: "Failed to update: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Saved" });
      setEditingId(null);
      setEditForm({});
      load();
    }
    setSaving(false);
  };

  const addCompany = async () => {
    const company_name = newForm.company_name.trim();
    if (!company_name) {
      toast({ title: "Error", description: "Company name is required", variant: "destructive" });
      return;
    }

    setSaving(true);
    const { error } = await supabase.from("tenant_taxicaller_companies").insert({
      tenant_id: DEFAULT_TENANT_ID,
      company_name,
      notes: newForm.notes.trim() || null,
      is_active: true,
    });

    if (error) {
      toast({
        title: "Error",
        description: error.code === "23505" ? "This exact company name is already mapped." : "Failed to add: " + error.message,
        variant: "destructive",
      });
    } else {
      toast({ title: "Added", description: `"${company_name}" now maps to this tenant.` });
      setShowAddForm(false);
      setNewForm(emptyNewForm);
      load();
    }
    setSaving(false);
  };

  const deleteCompany = async (c: CompanyRow) => {
    if (!confirm(`Remove "${c.company_name}"? Jobs reporting this company will fall back to the default tenant instead.`)) return;
    const { error } = await supabase.from("tenant_taxicaller_companies").delete().eq("id", c.id);
    if (error) {
      toast({ title: "Error", description: "Failed to delete: " + error.message, variant: "destructive" });
    } else {
      toast({ title: "Removed" });
      load();
    }
  };

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-3">
        <h4 className="font-medium text-foreground flex items-center gap-2">
          <Webhook className="h-4 w-4 text-yellow-500" />
          Webhook URL
        </h4>
        <p className="text-xs text-muted-foreground">
          Give this URL to a new company's TaxiCaller account (or your own, if adding another company under the
          same account) so their job data flows into this tenant. Only needed when a company isn't already covered
          by the mapping below.
        </p>
        <div className="flex items-center gap-2">
          <Input readOnly value={WEBHOOK_URL} className="font-mono text-xs" onFocus={(e) => e.target.select()} />
          <Button size="sm" variant="outline" onClick={copyWebhookUrl} className="shrink-0">
            {copied ? <Check className="w-4 h-4 mr-1" /> : <Copy className="w-4 h-4 mr-1" />}
            {copied ? "Copied" : "Copy"}
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-yellow-900/20 bg-black/20 p-4 space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h4 className="font-medium text-foreground">TaxiCaller Company Mapping</h4>
            <p className="text-xs text-muted-foreground mt-1">
              When a job or webhook arrives without a tenant specified in the URL, DMS looks up the company name
              TaxiCaller sent (e.g. "Specialty Transport Services") against this list to find the right tenant --
              lets one shared TaxiCaller account cover several company identities. The exact spelling must match
              what TaxiCaller sends. Unmapped companies fall back to this tenant, same as today.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setShowAddForm((s) => !s)}
            className="border-yellow-600 text-yellow-400 hover:bg-yellow-900 shrink-0"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add Company
          </Button>
        </div>

        {showAddForm && (
          <div className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
            <div>
              <Label className="text-yellow-500 text-xs">Company Name (exact match from TaxiCaller)</Label>
              <Input
                value={newForm.company_name}
                onChange={(e) => setNewForm((f) => ({ ...f, company_name: e.target.value }))}
                placeholder="e.g. Whitestone Taxis"
              />
            </div>
            <div>
              <Label className="text-yellow-500 text-xs">Notes (optional)</Label>
              <Textarea
                value={newForm.notes}
                onChange={(e) => setNewForm((f) => ({ ...f, notes: e.target.value }))}
                rows={2}
              />
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1" />
              <Button size="sm" variant="ghost" onClick={() => { setShowAddForm(false); setNewForm(emptyNewForm); }}>
                <X className="w-4 h-4 mr-1" />
                Cancel
              </Button>
              <Button size="sm" onClick={addCompany} disabled={saving} className="golden-button">
                <Save className="w-4 h-4 mr-1" />
                Add
              </Button>
            </div>
          </div>
        )}

        {loading ? (
          <p className="text-sm text-muted-foreground">Loading...</p>
        ) : companies.length === 0 ? (
          <p className="text-sm text-muted-foreground">No companies mapped yet.</p>
        ) : (
          <div className="space-y-2">
            {companies.map((c) =>
              editingId === c.id ? (
                <div key={c.id} className="space-y-3 p-3 rounded-lg border border-gray-600 bg-gray-800/50">
                  <div>
                    <Label className="text-yellow-500 text-xs">Company Name</Label>
                    <Input
                      value={editForm.company_name || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, company_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label className="text-yellow-500 text-xs">Notes</Label>
                    <Textarea
                      value={editForm.notes || ""}
                      onChange={(e) => setEditForm((f) => ({ ...f, notes: e.target.value }))}
                      rows={2}
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={editForm.is_active ?? true}
                      onCheckedChange={(v) => setEditForm((f) => ({ ...f, is_active: !!v }))}
                    />
                    <Label className="text-sm">Active</Label>
                    <div className="flex-1" />
                    <Button size="sm" variant="ghost" onClick={cancelEdit}>
                      <X className="w-4 h-4 mr-1" />
                      Cancel
                    </Button>
                    <Button size="sm" onClick={saveEdit} disabled={saving} className="golden-button">
                      <Save className="w-4 h-4 mr-1" />
                      Save
                    </Button>
                  </div>
                </div>
              ) : (
                <div
                  key={c.id}
                  className={`p-3 rounded-lg border border-gray-700 flex items-start justify-between gap-3 ${
                    c.is_active ? "bg-gray-800/30" : "bg-gray-900/40 opacity-60"
                  }`}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-medium text-foreground">{c.company_name}</p>
                      {!c.is_active && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">Inactive</span>
                      )}
                    </div>
                    {c.notes && <p className="text-xs text-muted-foreground mt-0.5">{c.notes}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => startEdit(c)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => deleteCompany(c)} className="text-red-400 hover:text-red-300">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default TaxiCallerCompaniesSettings;
